import { faMinusCircle, faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldArray, useFormikContext } from 'formik';
import type { FC } from 'react';
import type { Link } from 'src/apis/api-utilities';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import { FieldLayout } from 'src/components/form/field-layout';
import { FileUpload } from 'src/components/form/file-upload';
import { FieldLabel } from 'src/components/form/label';
import type { StorageOfferFormValues } from 'src/pages/storage/offers-new/create/schema';

type AttachFilesFieldsProps = {
  prepareFileUploadLink: Link;
};

export const AttachFilesFields: FC<AttachFilesFieldsProps> = ({
  prepareFileUploadLink,
}) => {
  const { values } = useFormikContext<StorageOfferFormValues>();
  const files = Array.isArray(values.attachedFiles) ? values.attachedFiles : [];

  return (
    <FieldLayout stacked={false} className="items-start">
      <FieldLabel
        name="attachedFiles"
        displayLabel="Attach Files"
        fieldId="attachedFiles"
      />
      <FieldArray
        name="attachedFiles"
        render={(arrayHelpers) => (
          <div className="grid gap-md">
            {files.map((_, index) => (
              <FileRow
                key={index}
                index={index}
                prepareFileUploadLink={prepareFileUploadLink}
                onRemove={() => arrayHelpers.remove(index)}
              />
            ))}
            <div className="mt-lg grid items-center justify-end">
              <Button
                type="button"
                onClick={() => arrayHelpers.push({ file: null })}
                mode="link"
                data-testid="add-more-files"
              >
                <FontAwesomeIcon icon={faPlusCircle} /> Add More
              </Button>
            </div>
          </div>
        )}
      />
    </FieldLayout>
  );
};

const FileRow: FC<{
  index: number;
  prepareFileUploadLink: Link;
  onRemove: () => void;
}> = ({ index, prepareFileUploadLink, onRemove }) => (
  <Card mode="highlight">
    <FileUpload
      stacked
      hideLabel
      name={`attachedFiles[${index}].file`}
      label="Attach File"
      localPreviewForUnauthenticatedUsers
      formats="pdf"
      accept=".pdf"
      getUploadUrl={prepareFileUploadLink.href}
    />
    {index > 0 && (
      <div className="mt-lg grid grid-flow-col justify-end">
        <Button
          type="button"
          mode="link"
          onClick={onRemove}
          data-testid={`remove-file-${index}`}
        >
          <FontAwesomeIcon icon={faMinusCircle} aria-hidden /> Remove File
        </Button>
      </div>
    )}
  </Card>
);
